import { FiChevronRight } from "react-icons/fi";

// Shared cell classes
const th = "px-3 py-4 text-[10px] font-bold uppercase bg-slate-50 dark:bg-slate-800/50 text-black dark:text-slate-300 border border-border-color dark:border-border-dark";
const td = "px-3 py-4 border border-border-color dark:border-border-dark";
const tdMono = `${td} font-mono`;

// Section heading with icon (same pattern as the existing page)
const SectionTitle = ({ icon, color, children }) => (
  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
    <span
      className={`flex items-center justify-center w-10 h-10 rounded-lg ${color}`}
    >
      <span className="material-symbols-outlined text-[24px]">{icon}</span>
    </span>
    {children}
  </h2>
);

// Generic table renderer for sections 2-4
const SimpleTable = ({ headers, rows, monoCols = [] }) => (
  <div className="overflow-x-auto border border-border-color dark:border-border-dark rounded-xl">
    <table className="w-full text-left border-collapse border-hidden">
      <thead className="text-[11px]">
        <tr>
          {headers.map((h) => (
            <th key={h} className={th}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody className="text-[11px]">
        {rows.map((row, i) => (
          <tr
            key={i}
            className={i % 2 === 0 ? "bg-white dark:bg-transparent" : "bg-gray-50/50 dark:bg-white/[0.02]"}
          >
            {row.map((cell, j) => (
              <td key={j} className={monoCols.includes(j) ? tdMono : td}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const SOFTWARE = "Docker v26.1.4, Docker Compose v2.28.1";
const OS = "Ubuntu 24.04 Minimal / RedHat 9 / CentOS 9 / Oracle Linux 9";

// Repeated "Source" values
const SRC_ACCESS = "Machines from where Netraa can be accessed.";
const SRC_CLIENT = "Client's App servers and DB Servers";
const SRC_AOP = "AOP server(Platfrom Engine), APM server.";

const softwareRows = [
  ["1", "Netraa APM", "Load balancer(LB)", SOFTWARE],
  ["2", "Netraa APM", "App server", SOFTWARE],
  ["3", "Netraa APM", "Netraa Cluster", SOFTWARE],
  ["4", "Netraa APM", "DB cluster", SOFTWARE],
];

const firewallRows = [
  ["1", "Netraa App server", "Netraa DB", "9200, 3306", "TCP", "Required to get data from DB."],
  ["2", "Load balancer(LB)","Netraa Cluster", "11800, 12800, 4444", "TCP", "Required to route data from LB to Netraa Cluster."],
  ["3", "Netraa Cluster", "Netraa DB", "9200, 3306", "TCP", "Required to store data in DB."],
  ["4", "App/DB Servers", "Load balancer(LB)", "11800", "TCP", "Agent need to communicate to Netraa from the servers need to be monitored."],
  ["5", "Web Servers", "Load balancer(LB)", "11800, 12800", "TCP", "Agent need to communicate to Netraa from the servers need to be monitored."],
  ["6", "Oracle DB Servers", "Load balancer(LB)", "4444", "TCP", "Agent need to communicate to Netraa from the servers need to be monitored."],
  ["7", "Load balancer(LB)", "Client's DB server", "Cloud db/ DB port", "TCP", "Required for accessing DB for agentless monitoring."],
  ["8", "Netraa Apm server", "SMTP server", "25 or any custom port configured.", "TCP", "Required send alert on mail."],
  ["9", "Internal users", "Netraa App server", "12001", "HTTP/TCP", "Required to access Netraa UI."],
];

const accessRows = [
  ["1", "VPN", "VPN Access", "VPN access enabled"],
  ["2", "VPN", "VPN Client", "Approved VPN client installed and configured"],
  ["3", "VPN", "VPN Credentials", "Valid VPN username/password or SSO access."],
  ["4", "VPN", "Network Access", "Required access to Netraa APM servers."],
  ["5", "Access", "Sudo Access", "Sudo privileges should be provided on all Netraa APM servers."],
  ["6", "File Transfer", "SFTP access", "SFTP access required to transfer Netraa builds to allocated servers."],
  ["7", "Temporary/Assisted access", "Access required for Agent installation", "Temporary/Assisted SUDO/Admin access during agent installation."],
  ["8", "SMTP Credentials", "SMTP domain with username and password", "Required to send alert on mail."],
  ["9", "User Creation (DB Monitoring)", "User Creation (DB)", "DB User required with Read only privelages (Check agent deployment section for user creation query.)"],
];

export function renderPrerequisites() {
  return (
    <div className="transition-opacity duration-300">

      {/* Breadcrumb */}
      <nav className="flex flex-wrap items-center gap-2 text-sm text-[#9a734c] mb-8">
        <a className="hover:text-primary hover:underline" href="#">Netraa APM</a>
        <FiChevronRight />
        <span>Deployment and Configuration</span>
        <FiChevronRight />
        <span className="text-[#1b140d] dark:text-white font-medium">
          Infrastructure Requirements
        </span>
      </nav>

      {/* Header */}
      <div className="mb-10">
        <span className="inline-block px-3 py-1 mb-4 text-xs font-bold tracking-wider text-orange-600 uppercase bg-orange-100 rounded-full dark:bg-orange-900/30 dark:text-orange-400">
          Documentation
        </span>
        <h1 className="text-4xl font-extrabold tracking-tight mb-4">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">
            Netraa{" "}
          </span>
          Infrastructure Requirements
        </h1>
        <p className="text-lg text-text-muted dark:text-gray-400 leading-relaxed">
          Environment sizing, software, firewall and access requirements for deploying the Netraa APM platform.
        </p>
      </div>

      {/* Main Card Wrapper */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-subtle">

        {/* 1. Environment Specifications Matrix */}
       <section className="mb-16 scroll-mt-24" id="master-sizing">
  <SectionTitle
    icon="dvr"
    color="bg-green-100 dark:bg-green-900/30 text-green-600 border border-green-200 dark:border-green-900/50"
  >
    Environment Specifications Matrix
  </SectionTitle>

  <div className="overflow-x-auto border border-border-color dark:border-border-dark rounded-xl">
    <table className="text-left border-collapse border-hidden">
      <thead className="text-[11px]">
        <tr>
          <th className={th}>Deployment Scale</th>
          <th className={th}>Component</th>
          <th className={`${th} text-center`}>Qty</th>
          <th className={th}>OS</th>
          <th className={th}>CPU (Cores)</th>
          <th className={th}>Memory (GB)</th>
          <th className={th}>Storage</th>
          <th className={th}>Source</th>
          <th className={th}>Destination</th>
          <th className={th}>Ports</th>
          <th className={th}>Softwares to be installed in the provisioned Netraa servers</th>
        </tr>
      </thead>

      <tbody className="text-[11px]">

        {/* Demo Env */}
        <tr className="bg-white dark:bg-transparent">
          <td className={`${td} font-bold text-primary`}>Demo Env</td>
          <td className={td}>Single VM</td>
          <td className={`${td} text-center`}>1</td>
          <td className={td}>Ubuntu 24.04+</td>
          <td className={tdMono}>6</td>
          <td className={tdMono}>16</td>
          <td className={td}>250 GB</td>
          <td className={td}>Machines from where Netraa can be accessed and clients application servers.</td>
          <td className={td}>Internal/Monitoring</td>
          <td className={tdMono}>12001, 11800</td>
          <td className={td}>{SOFTWARE}</td>
        </tr>

        {/* 10 Entities */}
        <tr className="bg-gray-50/50 dark:bg-white/[0.02]">
          <td className={`${td} font-bold align-top`} rowSpan={3}>10 Entities</td>
          <td className={td}>App Server</td>
          <td className={`${td} text-center`}>1</td>
          <td className={td} rowSpan={3}>{OS}</td>
          <td className={tdMono}>8</td>
          <td className={tdMono}>16</td>
          <td className={td}>100 GB SSD</td>
          <td className={td}>{SRC_ACCESS}</td>
          <td className={td}>APM Server</td>
          <td className={tdMono}>80, 443, 12001</td>
          <td className={td} rowSpan={3}>{SOFTWARE}</td>
        </tr>
        <tr className="bg-gray-50/50 dark:bg-white/[0.02]">
          <td className={td}>Netraa Cluster</td>
          <td className={`${td} text-center`}>1</td>
          <td className={tdMono}>6</td>
          <td className={tdMono}>12</td>
          <td className={td}>200 GB SSD</td>
          <td className={td}>{SRC_CLIENT}</td>
          <td className={td}>Platform Engine</td>
          <td className={tdMono}>11800, 12800, 4444</td>
        </tr>
        <tr className="bg-gray-50/50 dark:bg-white/[0.02]">
          <td className={td}>DB Cluster</td>
          <td className={`${td} text-center`}>1</td>
          <td className={tdMono}>8</td>
          <td className={tdMono}>16</td>
          <td className={td}>500 GB NVMe</td>
          <td className={td}>{SRC_AOP}</td>
          <td className={td}>ES + MySQL</td>
          <td className={tdMono}>9200, 3306</td>
        </tr>

        {/* 50 Entities */}
        <tr>
          <td className={`${td} font-bold align-top`} rowSpan={3}>50 Entities</td>
          <td className={td}>App Server</td>
          <td className={`${td} text-center`}>1</td>
          <td className={td} rowSpan={3}>{OS}</td>
          <td className={tdMono}>8</td>
          <td className={tdMono}>16</td>
          <td className={td}>250 GB SSD</td>
          <td className={td}>{SRC_ACCESS}</td>
          <td className={td}>UI + Microservices</td>
          <td className={tdMono}>80, 443, 8080, 12001</td>
          <td className={td} rowSpan={3}>{SOFTWARE}</td>
        </tr>
        <tr>
          <td className={td}>Netraa Cluster</td>
          <td className={`${td} text-center`}>1</td>
          <td className={tdMono}>16</td>
          <td className={tdMono}>32</td>
          <td className={td}>500 GB SSD</td>
          <td className={td}>{SRC_CLIENT}</td>
          <td className={td}>Platform Engine</td>
          <td className={tdMono}>11800, 12800, 4444</td>
        </tr>
        <tr>
          <td className={td}>DB Cluster</td>
          <td className={`${td} text-center`}>2</td>
          <td className={tdMono}>16</td>
          <td className={tdMono}>32</td>
          <td className={td}>2 TB NVMe</td>
          <td className={td}>{SRC_AOP}</td>
          <td className={td}>Netraa DB cluster</td>
          <td className={tdMono}>9200, 9300, 3306</td>
        </tr>

        {/* 100 Entities */}
        <tr className="bg-gray-50/50 dark:bg-white/[0.02]">
          <td className={`${td} font-bold align-top`} rowSpan={4}>100 Entities</td>
          <td className={td}>Load balancer(LB)</td>
          <td className={`${td} text-center`}>1</td>
          <td className={td} rowSpan={4}>{OS}</td>
          <td className={tdMono}>4</td>
          <td className={tdMono}>8</td>
          <td className={td}>150 GB</td>
          <td className={td}>{SRC_CLIENT}</td>
          <td className={td}>Load balancer</td>
          <td className={tdMono}>11800, 12800</td>
          <td className={td} rowSpan={4}>{SOFTWARE}</td>
        </tr>
        <tr className="bg-gray-50/50 dark:bg-white/[0.02]">
          <td className={td}>App server</td>
          <td className={`${td} text-center`}>1</td>
          <td className={tdMono}>12</td>
          <td className={tdMono}>24</td>
          <td className={td}>150 GB</td>
          <td className={td}>{SRC_ACCESS}</td>
          <td className={td}>APM Server</td>
          <td className={tdMono}>12001</td>
        </tr>
        <tr className="bg-gray-50/50 dark:bg-white/[0.02]">
          <td className={td}>Netraa Cluster</td>
          <td className={`${td} text-center`}>2</td>
          <td className={tdMono}>12</td>
          <td className={tdMono}>24</td>
          <td className={td}>150 GB</td>
          <td className={td}>Load balancer</td>
          <td className={td}>Netraa AOP cluster</td>
          <td className={tdMono}>11800, 12800, 4444</td>
        </tr>
        <tr className="bg-gray-50/50 dark:bg-white/[0.02]">
          <td className={td}>DB cluster</td>
          <td className={`${td} text-center`}>2</td>
          <td className={tdMono}>16</td>
          <td className={tdMono}>32</td>
          <td className={td}>3.5 TB</td>
          <td className={td}>Netraa AOP cluster, APM server</td>
          <td className={td}>Netraa DB cluster</td>
          <td className={tdMono}>9200, 9300, 3306</td>
        </tr>
      </tbody>
    </table>
  </div>
</section>
        {/* 2. Software Requirements */}
        <section className="mb-16 scroll-mt-24" id="software-requirements">
          <SectionTitle
            icon="terminal"
            color="bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 border border-cyan-200 dark:border-cyan-900/50"
          >
            2. Software Requirements
          </SectionTitle>
          <SimpleTable
            headers={["Sr No", "Application", "Server", "Software"]}
            rows={softwareRows}
          />
        </section>

        {/* 3. Firewall Policy */}
        <section className="mb-16 scroll-mt-24" id="firewall-policy">
          <SectionTitle
            icon="shield"
            color="bg-orange-100 dark:bg-orange-900/30 text-primary border border-orange-200 dark:border-orange-900/50"
          >
            3. Firewall Policy
          </SectionTitle>
          <p className="text-sm text-text-muted mb-4">Firewall / Port Opening</p>
          <SimpleTable
            headers={["Sr No.", "Source", "Destination", "Destination Port", "Protocol", "Purpose"]}
            rows={firewallRows}
            monoCols={[3]}
          />
        </section>

        {/* 4. Access */}
        <section className="mb-10 scroll-mt-24" id="access">
          <SectionTitle
            icon="key"
            color="bg-blue-100 dark:bg-blue-900/30 text-blue-600 border border-blue-200 dark:border-blue-900/50"
          >
            4. Access
          </SectionTitle>
          <SimpleTable
            headers={["Sr. No.", "Category", "Requirement", "Details"]}
            rows={accessRows}
          />
        </section>

      </div>
    </div>
  );
}